import { useState } from 'react'
import { Box, Button, FormControlLabel, Stack, Switch, Typography, useMediaQuery, useTheme } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

interface StackBlockProps {
  title: string
  text: string | null | undefined
}

/**
 * A stack trace, monospaced, scrollable in its own box and copyable.
 *
 * On desktop it scrolls both ways rather than wrapping: a wrapped stack frame loses the column
 * alignment that makes a trace scannable. On a phone the box is too narrow for that to help (every
 * frame is cut off and needs a sideways drag), so wrapping is the default there. Either way the
 * "Wrap lines" switch flips it.
 */
export function StackBlock({ title, text }: StackBlockProps) {
  const [copied, setCopied] = useState(false)
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  // null = follow the screen size; set once the user touches the switch.
  const [wrapChoice, setWrapChoice] = useState<boolean | null>(null)
  const wrap = wrapChoice ?? isPhone

  const copy = async () => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard can be refused (non-secure context); the text is selectable either way.
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {text && (
          <FormControlLabel
            control={<Switch size="small" checked={wrap} onChange={(e) => setWrapChoice(e.target.checked)} />}
            label={<Typography variant="body2">Wrap lines</Typography>}
            sx={{ mr: 0 }}
          />
        )}
        {text && (
          <Button
            size="small"
            onClick={copy}
            startIcon={copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </Stack>
      {text ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: wrap ? 'pre-wrap' : 'pre',
            overflowWrap: wrap ? 'anywhere' : 'normal',
            overflow: 'auto',
            maxHeight: 360,
            maxWidth: '100%',
            userSelect: 'text',
          }}
        >
          {text}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not captured for this report.
        </Typography>
      )}
    </Box>
  )
}
